import type { Message, Part } from "@mux/sdk/v2/client"

const compareID = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

const sequence = (part: Part) => {
  const value = (part as Part & { sequence?: unknown }).sequence
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined
}

export const cmpPart = (a: Part, b: Part) => {
  const left = sequence(a)
  const right = sequence(b)
  if (left !== undefined || right !== undefined) {
    if (left === undefined) return -1
    if (right === undefined) return 1
    if (left !== right) return left - right
  }
  // Parts without a sequence (prompt, file, and other unstreamed parts) keep
  // their source order; snapshots and the event stream are already chronological.
  return 0
}

// A prompt waiting for its run follows everything that has started. Its run
// republishes it, unmarked, with the time it started.
export const isWaitingPrompt = (message: Message) => (message as Message & { waiting?: boolean }).waiting === true

export const cmpMessage = (a: Message, b: Message) => {
  const order = Number(isWaitingPrompt(a)) - Number(isWaitingPrompt(b))
  if (order) return order
  const delta = a.time.created - b.time.created
  return delta || compareID(a.id, b.id)
}

export function sortParts(parts: Part[]) {
  return parts.filter((part) => !!part?.id).sort(cmpPart)
}

/** An earlier turn's outline stands for the parts it folds; see the outline part renderer. */
export const isOutlinePart = (part: Part) => {
  // The server's outline part is not in the SDK's part union.
  const type: string = part.type
  return type === "outline"
}

// Only compatible prefixes are append-only growth. Divergent text is an edit,
// whose authority is decided by the snapshot/event ordering boundary.
export function mergeStreamingPart(current: Part, incoming: Part): Part {
  if (((current.type === "text" && incoming.type === "text") ||
       (current.type === "reasoning" && incoming.type === "reasoning")) &&
      current.text.length > incoming.text.length && current.text.startsWith(incoming.text)) {
    return { ...incoming, text: current.text }
  }
  return incoming
}

const keepStreamedText = (databasePart: Part, livePart: Part) => {
  if (databasePart.type === "tool" && livePart.type === "tool" &&
    (livePart.state.status === "completed" || livePart.state.status === "error") &&
    (databasePart.state.status === "pending" || databasePart.state.status === "running")) return livePart
  return mergeStreamingPart(livePart, databasePart)
}

export function mergeIncompleteAssistantParts(liveParts: Part[] | undefined, databaseParts: Part[]) {
  if (!liveParts?.length) return databaseParts
  const liveByID = new Map(liveParts.map((part) => [part.id, part]))
  const merged = databaseParts.map((databasePart) => {
    const livePart = liveByID.get(databasePart.id)
    liveByID.delete(databasePart.id)
    return livePart ? keepStreamedText(databasePart, livePart) : databasePart
  })
  return sortParts([...merged, ...liveByID.values()])
}

// Completion does not imply the snapshot caught up with the stream it completed.
// Aborts finalize a message while streamed text/reasoning is still between
// persistence checkpoints, so the snapshot owns structure while the stream still
// owns length until an explicit removal event says otherwise.
export function mergeCompletedAssistantParts(liveParts: Part[] | undefined, databaseParts: Part[]) {
  if (!liveParts?.length) return databaseParts
  const liveByID = new Map(liveParts.map((part) => [part.id, part]))
  let changed = false
  const merged = databaseParts.map((databasePart) => {
    const livePart = liveByID.get(databasePart.id)
    liveByID.delete(databasePart.id)
    if (!livePart) return databasePart
    const next = keepStreamedText(databasePart, livePart)
    if (next !== databasePart) changed = true
    return next
  })
  const missing = [...liveByID.values()].filter((part) => {
    if (part.type === "tool") return part.state.status === "completed" || part.state.status === "error"
    return (part.type === "text" || part.type === "reasoning") && part.text.length > 0
  })
  if (!changed && missing.length === 0) return databaseParts
  return sortParts([...merged, ...missing])
}

// Optimistic user parts are owned by the optimistic lifecycle, not by these
// reducers: replaying live-only user parts here would duplicate a prompt whose
// server-assigned part IDs differ from the client's.
export function mergeMessageParts(message: Message | undefined, liveParts: Part[] | undefined, databaseParts: Part[]) {
  if (message?.role !== "assistant") return databaseParts
  if (typeof message.time.completed !== "number") return mergeIncompleteAssistantParts(liveParts, databaseParts)
  const outline = databaseParts.find(isOutlinePart)
  if (!outline) return mergeCompletedAssistantParts(liveParts, databaseParts)
  const shown = databaseParts.filter((part) => part !== outline)
  // A reply already held in full stays expanded, so a reload never collapses
  // rows on screen; otherwise the outline replaces the parts it folds.
  if (!liveParts || !holdsFoldedReply(liveParts, shown, outline)) return databaseParts
  const held = new Map(liveParts.map((part) => [part.id, part]))
  const merged = shown.map((part) => {
    const live = held.get(part.id)
    held.delete(part.id)
    return live ? keepStreamedText(part, live) : part
  })
  return sortParts([...merged, ...held.values()])
}

// The held parts are the whole finished reply: every part the outlined
// snapshot shows, and every call it folds, each finished. The outline folds
// every call it does not show, so the calls held must number its count plus
// the calls shown. Anything less was missed and the snapshot repairs it.
function holdsFoldedReply(liveParts: Part[], shown: Part[], outline: Part) {
  if (liveParts.some(isOutlinePart)) return false
  const held = new Set(liveParts.map((part) => part.id))
  if (!shown.every((part) => held.has(part.id))) return false
  const calls = liveParts.filter((part) => part.type === "tool")
  if (!calls.every((part) => part.state.status === "completed" || part.state.status === "error")) return false
  const folded = "calls" in outline ? outline.calls : undefined
  return typeof folded === "number" && calls.length === folded + shown.filter((part) => part.type === "tool").length
}

export function mergeIncompleteAssistantMessages(liveMessages: Message[] | undefined, databaseMessages: Message[]) {
  if (!liveMessages?.length) return databaseMessages
  const liveByID = new Map(liveMessages.map((message) => [message.id, message]))
  return databaseMessages.map((databaseMessage) => {
    if (databaseMessage.role !== "assistant" || typeof databaseMessage.time.completed === "number") return databaseMessage
    const liveMessage = liveByID.get(databaseMessage.id)
    if (liveMessage?.role === "assistant" && typeof liveMessage.time.completed === "number") return liveMessage
    if (liveMessage?.role !== "assistant" || liveMessage.tokens.input + liveMessage.tokens.output <= 0) return databaseMessage
    return { ...databaseMessage, tokens: liveMessage.tokens, cost: liveMessage.cost }
  })
}

/** The error a chat already known to be gone fails with, as its server would. */
export function sessionNotFoundError() {
  return Object.assign(new Error("Session not found"), { code: "SESSION_NOT_FOUND" })
}

export function isSessionNotFoundError(error: unknown) {
  if (!error || typeof error !== "object") return false
  const outer = error as { code?: unknown; error?: unknown }
  if (outer.code === "SESSION_NOT_FOUND") return true
  return !!outer.error && typeof outer.error === "object" &&
    (outer.error as { code?: unknown }).code === "SESSION_NOT_FOUND"
}

export function mergeMessagesSorted(a: readonly Message[], b: readonly Message[]) {
  const byID = new Map<string, Message>()
  for (const message of a) byID.set(message.id, message)
  for (const message of b) byID.set(message.id, message)
  return [...byID.values()].sort(cmpMessage)
}
